import { Observable } from '@apollo/client/core';
import { onError } from '@apollo/client/link/error';

import { clearLocalAuthentication } from 'remote/auth/cognito';
import { forceRefreshGetCurrentSession } from 'remote/auth/get-current-session';
import {
  AuthTransitionReason,
  getAuthSessionResetContext,
  runAuthSessionTransition,
} from 'state/auth/session-transition';
import logger from 'utils/logger';

const AUTH_REFRESH_ATTEMPTED_KEY = 'authRefreshAttempted';

interface RefreshOutcome {
  session: Awaited<ReturnType<typeof forceRefreshGetCurrentSession>> | null;
  failed: boolean;
}

let pendingAuthRefreshTransition: Promise<RefreshOutcome> | null = null;

const refreshSessionThroughTransition = () => {
  if (pendingAuthRefreshTransition) {
    return pendingAuthRefreshTransition;
  }

  let failed = false;
  pendingAuthRefreshTransition = runAuthSessionTransition({
    reason: AuthTransitionReason.ForcedRefresh,
    commitCredentials: async () => {
      try {
        return await forceRefreshGetCurrentSession();
      } catch (_) {
        failed = true;
        return clearLocalAuthentication();
      }
    },
  })
    .then(session => ({ session, failed }))
    .finally(() => {
      pendingAuthRefreshTransition = null;
    });

  return pendingAuthRefreshTransition;
};

// Generally the app doesn't attempt to make requests that fail unauthorized but it happens to ~5K users a week. Try to recover.
export const refreshAndRetryBadAuthLink = onError(({ graphQLErrors, forward, operation }) => {
  const isUnauthenticatedError = graphQLErrors?.some(
    x => x?.extensions?.code === 'UNAUTHENTICATED'
  );

  if (
    isUnauthenticatedError &&
    !getAuthSessionResetContext() &&
    !operation.getContext()[AUTH_REFRESH_ATTEMPTED_KEY]
  ) {
    operation.setContext({ [AUTH_REFRESH_ATTEMPTED_KEY]: true });

    return new Observable(observer => {
      let isActive = true;
      let retrySubscription: { unsubscribe(): void } | undefined;

      refreshSessionThroughTransition()
        .then(({ session, failed }) => {
          if (!isActive) {
            return;
          }

          if (failed || !session) {
            logger.error({
              operationName: operation.operationName,
              message: 'Cannot retry request - forceRefreshGetCurrentSession failed',
              isForceRefreshUserSession: true,
            });
            observer.error(graphQLErrors);
            return;
          }

          logger.info({
            message: 'Retrying request - forceRefreshGetCurrentSession succeeded',
            operationName: operation.operationName,
            isForceRefreshUserSession: true,
          });

          operation.setContext(({ headers = {} }) => ({
            headers: {
              ...headers,
              authorization: `Bearer ${session.getIdToken().getJwtToken()}`,
            },
          }));

          // Retry the request
          const subscriber = {
            next: observer.next.bind(observer),
            error: observer.error.bind(observer),
            complete: observer.complete.bind(observer),
          };
          retrySubscription = forward(operation).subscribe(subscriber);
        })
        .catch(() => {
          if (!isActive) {
            return;
          }
          logger.error({
            operationName: operation.operationName,
            message: 'Cannot retry request - authentication transition failed',
            isForceRefreshUserSession: true,
          });
          observer.error(graphQLErrors);
        });

      return () => {
        isActive = false;
        retrySubscription?.unsubscribe();
      };
    });
  }

  return undefined;
});
